/**
 * Wraps lesson content. When active (whiteboard on), uses a fixed 1000×800 logical
 * size and scale() so tutor/student align. When inactive, fills space with normal
 * scroll/zoom so focus mode behaves as before. Same wrapper in both modes so the
 * lesson never remounts when toggling whiteboard (avoids "Continue learning" reset).
 */

import React, { useRef, useState, useEffect } from 'react';
import { LOGICAL_WIDTH, LOGICAL_HEIGHT } from './constants';

const LOGICAL_W = LOGICAL_WIDTH;
const LOGICAL_H = LOGICAL_HEIGHT;

export function ScaledLessonContent({ children, containerRef, outerContainerRef, active = false }) {
  const innerOuterRef = useRef(null);
  const outerRef = outerContainerRef ?? innerOuterRef;
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!active) return;
    const el = outerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (!w || !h) return;
      setScale(Math.min(w / LOGICAL_W, h / LOGICAL_H));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [active]);

  if (!active) {
    return (
      <div
        ref={outerRef}
        style={{
          width: '100%',
          height: '100%',
          overflow: 'auto',
          position: 'relative',
        }}
      >
        <div
          ref={containerRef}
          style={{
            width: '100%',
            height: '100%',
            minHeight: 0,
          }}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={outerRef}
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        ref={containerRef}
        style={{
          width: LOGICAL_W,
          height: LOGICAL_H,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'absolute',
          top: '50%',
          left: '50%',
          marginTop: -(LOGICAL_H * scale) / 2,
          marginLeft: -(LOGICAL_W * scale) / 2,
          overflow: 'hidden',
          // Prevent iOS from selecting / highlighting lesson text through the canvas overlay.
          // The lesson content is non-interactive while drawing mode is active anyway.
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default ScaledLessonContent;
